
import { supabase } from "@/integrations/supabase/client";
import { BoardCategory, Post } from "@/types/board";

// 향상된 캐시 시스템
interface CacheItem {
  data: any;
  timestamp: number;
  expiresAt: number;
}

const cache = new Map<string, CacheItem>();
const CACHE_DURATION = 60000; // 1분으로 증가
const MAX_CACHE_SIZE = 50; // 캐시 크기 제한

const getCacheKey = (category?: BoardCategory, limit?: number) => 
  `posts_${category || 'all'}_${limit || 'unlimited'}`;

const isValidCache = (item: CacheItem) => {
  return Date.now() < item.expiresAt;
};

const cleanCache = () => {
  if (cache.size > MAX_CACHE_SIZE) {
    const entries = Array.from(cache.entries());
    entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
    entries.slice(0, Math.floor(MAX_CACHE_SIZE / 2)).forEach(([key]) => {
      cache.delete(key);
    });
  }
};

export const getPosts = async (category?: BoardCategory, limit = 50, retryCount = 0): Promise<Post[]> => {
  const cacheKey = getCacheKey(category, limit);
  const cached = cache.get(cacheKey);
  
  // 캐시된 데이터가 있고 유효하면 반환
  if (cached && isValidCache(cached)) {
    return cached.data;
  }

  try {
    let query = supabase
      .from('board_posts')
      .select('id, title, content, category, author_name, author_id, author_avatar, created_at, updated_at, view_count, like_count, comment_count')
      .order('created_at', { ascending: false })
      .limit(limit);
      
    if (category) {
      query = query.eq('category', category);
    }
    
    const { data, error } = await query;
    
    if (error) {
      console.error('Error fetching posts:', error);
      
      // 타임아웃 에러인 경우 재시도
      if (error.code === '57014' && retryCount < 2) {
        console.log(`Retrying getPosts (attempt ${retryCount + 1})`);
        await new Promise(resolve => setTimeout(resolve, 500 * (retryCount + 1)));
        return getPosts(category, limit, retryCount + 1);
      }
      
      // 캐시된 데이터가 있으면 반환 (만료되었어도)
      if (cached) {
        console.log('Using expired cache due to error');
        return cached.data;
      }
      
      throw error;
    }
    
    const posts = data as Post[];
    
    // 결과를 캐시에 저장 (만료 시간 포함)
    const now = Date.now();
    cache.set(cacheKey, { 
      data: posts, 
      timestamp: now,
      expiresAt: now + CACHE_DURATION
    });
    
    // 캐시 크기 관리
    cleanCache();
    
    return posts;
  } catch (error) {
    console.error('Failed to fetch posts:', error);
    
    // 캐시된 데이터가 있으면 반환 (만료되었어도)
    if (cached) {
      console.log('Using expired cache due to error');
      return cached.data;
    }
    
    // 빈 배열 반환 (UI가 깨지지 않도록)
    return [];
  }
};

export const getPostById = async (id: string) => {
  const cacheKey = `post_${id}`;
  const cached = cache.get(cacheKey);
  
  if (cached && isValidCache(cached)) {
    await incrementViewCount(id, cached.data.view_count);
    return cached.data;
  }

  const { data, error } = await supabase
    .from('board_posts')
    .select('*')
    .eq('id', id)
    .single();
    
  if (error) {
    console.error('Error fetching post:', error);
    throw error;
  }
  
  // 캐시에 저장
  const now = Date.now();
  cache.set(cacheKey, {
    data: data as Post,
    timestamp: now,
    expiresAt: now + CACHE_DURATION
  });
  
  await incrementViewCount(id, data.view_count);
  
  return data as Post;
};

export const getCommentsByPostId = async (postId: string) => {
  const cacheKey = `comments_${postId}`;
  const cached = cache.get(cacheKey);
  
  if (cached && isValidCache(cached)) {
    return cached.data;
  }

  const { data, error } = await supabase
    .from('post_comments')
    .select('*')
    .eq('post_id', postId)
    .order('created_at', { ascending: true });
    
  if (error) {
    console.error('Error fetching comments:', error);
    throw error;
  }
  
  // 캐시에 저장
  const now = Date.now();
  cache.set(cacheKey, {
    data,
    timestamp: now,
    expiresAt: now + (CACHE_DURATION / 2) // 댓글은 더 짧은 캐시 시간
  });
  
  return data;
};

export const addComment = async (postId: string, content: string, user: any) => {
  const { data: profileData, error: profileError } = await supabase
    .from('profiles')
    .select('username')
    .eq('id', user.id)
    .single();
  
  let username;
  if (profileError) {
    console.error('Error fetching profile:', profileError);
    username = user.email?.split('@')[0] || 'Anonymous';
  } else {
    username = profileData.username || user.email?.split('@')[0] || 'Anonymous';
  }
  
  const commentData = {
    post_id: postId,
    content,
    author_id: user.id,
    author_name: username,
    author_avatar: user.user_metadata?.avatar_url || '/placeholder.svg'
  };
  
  const { data: comment, error: commentError } = await supabase
    .from('post_comments')
    .insert(commentData)
    .select()
    .single();
    
  if (commentError) {
    console.error('Error adding comment:', commentError);
    throw commentError;
  }
  
  const { data: post } = await supabase
    .from('board_posts')
    .select('comment_count')
    .eq('id', postId)
    .single();
    
  const { error: updateError } = await supabase
    .from('board_posts')
    .update({ comment_count: (post.comment_count || 0) + 1 })
    .eq('id', postId);
    
  if (updateError) {
    console.error('Error updating comment count:', updateError);
  }
  
  // 관련 캐시 무효화
  invalidatePostCache(postId);
  
  return comment;
};

export const createPost = async (
  title: string, 
  content: string, 
  category: BoardCategory, 
  user: any | null
) => {
  const postData: any = {
    title,
    content,
    category,
    author_name: 'Anonymous'
  };
  
  if (user) {
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('username')
      .eq('id', user.id)
      .single();
      
    if (profileError) {
      console.error('Error fetching user profile:', profileError);
    }
    
    const username = profile?.username || user.email?.split('@')[0] || 'Anonymous';
    
    postData.author_id = user.id;
    postData.author_name = username;
    postData.author_avatar = user.user_metadata?.avatar_url || '/placeholder.svg';
  }
  
  const { data, error } = await supabase
    .from('board_posts')
    .insert(postData)
    .select()
    .single();
    
  if (error) {
    console.error('Error creating post:', error);
    throw error;
  }
  
  // 캐시 무효화
  invalidateListCache();
  
  return data;
};

export const toggleLike = async (postId: string, currentLikes: number) => {
  const { error } = await supabase
    .from('board_posts')
    .update({ like_count: currentLikes })
    .eq('id', postId);
    
  if (error) {
    console.error('Error updating like count:', error);
    throw error;
  }
  
  // 캐시 무효화
  invalidatePostCache(postId);
  
  return true;
};

export const toggleCommentLike = async (commentId: string, currentLikes: number) => {
  const { error } = await supabase
    .from('post_comments')
    .update({ like_count: currentLikes })
    .eq('id', commentId);
    
  if (error) {
    console.error('Error updating comment like count:', error);
    throw error;
  }
  
  return true;
};

const incrementViewCount = async (postId: string, currentViews: number) => {
  const randomIncrement = Math.floor(Math.random() * 12) + 4;
  
  try {
    const { error } = await supabase
      .from('board_posts')
      .update({ view_count: currentViews + randomIncrement })
      .eq('id', postId);
      
    if (error) {
      console.error('Error updating view count:', error);
    }
  } catch (error) {
    console.error('Failed to increment view count:', error);
  }
};

export const getUserPosts = async (userId: string, page = 1, limit = 10) => {
  const start = (page - 1) * limit;
  const end = start + limit - 1;
  
  const { data, error, count } = await supabase
    .from('board_posts')
    .select('*', { count: 'exact' })
    .eq('author_id', userId)
    .order('created_at', { ascending: false })
    .range(start, end);
    
  if (error) {
    console.error('Error fetching user posts:', error);
    throw error;
  }
  
  return { 
    posts: data as Post[], 
    totalCount: count || 0
  };
};

export const getUserComments = async (userId: string, page = 1, limit = 10) => {
  const start = (page - 1) * limit;
  const end = start + limit - 1;
  
  const { data, error, count } = await supabase
    .from('post_comments')
    .select(`
      *,
      board_posts(title)
    `, { count: 'exact' })
    .eq('author_id', userId)
    .order('created_at', { ascending: false })
    .range(start, end);
    
  if (error) {
    console.error('Error fetching user comments:', error);
    throw error;
  }
  
  return {
    comments: data,
    totalCount: count || 0
  };
};

export const deletePost = async (postId: string) => {
  const { error } = await supabase
    .from('board_posts')
    .delete()
    .eq('id', postId);
    
  if (error) {
    console.error('Error deleting post:', error);
    throw error;
  }
  
  // 캐시 무효화
  invalidateListCache();
  invalidatePostCache(postId);
  
  return true;
};

export const updatePost = async (
  postId: string,
  title: string,
  content: string,
  category: BoardCategory
) => {
  const { data, error } = await supabase
    .from('board_posts')
    .update({ 
      title, 
      content, 
      category,
      updated_at: new Date().toISOString()
    })
    .eq('id', postId)
    .select()
    .single();
    
  if (error) {
    console.error('Error updating post:', error);
    throw error;
  }
  
  // 캐시 무효화
  invalidateListCache();
  invalidatePostCache(postId);
  
  return data;
};

// 캐시 무효화 헬퍼 함수들
const invalidateListCache = () => {
  const keysToDelete = Array.from(cache.keys()).filter(key => 
    key.startsWith('posts_') && !key.startsWith('post_')
  );
  keysToDelete.forEach(key => cache.delete(key));
};

const invalidatePostCache = (postId: string) => {
  cache.delete(`post_${postId}`);
  cache.delete(`comments_${postId}`);
};

export { 
  incrementViewCount
};
